import React, { useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, FileSpreadsheet, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/axios';
import { EstadoBadge } from './EstadoBadge';
import { ESTADOS, fmtDuracion, fmtFechaHora, haceCuanto, dispositivoTxt } from './actividadUtils';

const FILTROS = [{ k: 'todos', l: 'Todos' }, ...Object.entries(ESTADOS).map(([k, v]) => ({ k, l: v.label }))];
const COLS = [
  { key: 'nombre', label: 'Usuario', tipo: 'texto' }, { key: 'email', label: 'Email', tipo: 'texto' },
  { key: 'rol', label: 'Rol', tipo: 'texto' }, { key: 'estado_txt', label: 'Estado', tipo: 'texto' },
  { key: 'ultimo_login', label: 'Último ingreso', tipo: 'texto' }, { key: 'ultima', label: 'Última actividad', tipo: 'texto' },
  { key: 'logins', label: 'Ingresos', tipo: 'numero' }, { key: 'vistas', label: 'Pantallas', tipo: 'numero' },
  { key: 'tiempo', label: 'Tiempo de uso', tipo: 'texto' }, { key: 'acciones', label: 'Acciones', tipo: 'numero' },
  { key: 'seccion_top', label: 'Sección más usada', tipo: 'texto' }, { key: 'ip', label: 'Última IP', tipo: 'texto' },
  { key: 'disp', label: 'Dispositivo', tipo: 'texto' },
];

const aFila = (u) => ({
  ...u, estado_txt: ESTADOS[u.estado]?.label, ultimo_login: fmtFechaHora(u.ultimo_login_at), ultima: fmtFechaHora(u.ultima_actividad_at),
  tiempo: fmtDuracion(u.tiempo_seg), ip: u.ultimo_dispositivo?.ip || u.ultimo_ip || '', disp: dispositivoTxt(u.ultimo_dispositivo),
  seccion_top: u.seccion_top || '',
});

export const UsuariosActividadTabla = ({ usuarios, dias, onVer }) => {
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [exportando, setExportando] = useState(false);
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return usuarios.filter((u) => (filtro === 'todos' || u.estado === filtro)
      && (!t || [u.nombre, u.email, u.rol].some((s) => (s || '').toLowerCase().includes(t))));
  }, [usuarios, q, filtro]);

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await api.post('/kpis/exportar-tabla', { titulo: `Actividad de usuarios (${dias} días)`, columnas: COLS, filas: visibles.map(aFila) }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: `actividad_usuarios_${dias}d.xlsx` }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo exportar a Excel'); } finally { setExportando(false); }
  };

  return (
    <Card className="bg-white border-slate-200 overflow-hidden" data-testid="act-usuarios-tabla">
      <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
        <div className="relative w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario, mail o rol" className="pl-9" data-testid="act-buscar-input" />
        </div>
        <div className="flex flex-wrap rounded-full bg-slate-50 border border-slate-200 p-1">
          {FILTROS.map((f) => (
            <button key={f.k} onClick={() => setFiltro(f.k)} data-testid={`act-filtro-${f.k}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors duration-200 ${filtro === f.k ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              {f.l}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={exportar} disabled={exportando || !visibles.length} data-testid="act-exportar-btn">
          {exportando ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5 text-emerald-600" />} Excel
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              {['Usuario', 'Estado', 'Último ingreso', 'Última actividad', 'Ingresos', 'Tiempo de uso', 'Acciones', 'Sección más usada', 'Desde dónde', ''].map((h) => (
                <th key={h} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibles.map((u) => (
              <tr key={u.id} onClick={() => onVer(u)} className="cursor-pointer hover:bg-slate-50/80 transition-colors duration-150" data-testid={`act-fila-${u.id}`}>
                <td className="px-4 py-3">
                  <p className={`font-semibold ${u.activo ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{u.nombre || u.email}</p>
                  <p className="text-xs text-slate-400">{u.email} · {u.rol}</p>
                </td>
                <td className="px-4 py-3"><EstadoBadge estado={u.estado} testid={`act-estado-${u.id}`} /></td>
                <td className="px-4 py-3 whitespace-nowrap text-slate-600 tabular-nums">{fmtFechaHora(u.ultimo_login_at)}</td>
                <td className="px-4 py-3 whitespace-nowrap"><span className="text-slate-700">{haceCuanto(u.ultima_actividad_at)}</span></td>
                <td className="px-4 py-3 tabular-nums">{u.logins}{u.logins_fallidos ? <span className="ml-1 text-xs text-rose-600">({u.logins_fallidos} fallido{u.logins_fallidos > 1 ? 's' : ''})</span> : null}</td>
                <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtDuracion(u.tiempo_seg)}</td>
                <td className="px-4 py-3 tabular-nums">{u.acciones}</td>
                <td className="px-4 py-3 max-w-[12rem] truncate text-slate-600" title={u.seccion_top || ''}>{u.seccion_top || '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                  <p>{dispositivoTxt(u.ultimo_dispositivo) || '—'}</p>
                  <p className="font-mono text-slate-400">{u.ultimo_dispositivo?.ip || u.ultimo_ip || ''}</p>
                </td>
                <td className="px-3 py-3 text-slate-300"><ChevronRight size={16} /></td>
              </tr>
            ))}
            {!visibles.length && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400" data-testid="act-tabla-vacia">No hay usuarios para este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default UsuariosActividadTabla;
